import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, MessageSquare, X } from 'lucide-react';
import {
  TESTER_FEEDBACK_MESSAGE_MAX,
  testerFeedbackEnabledFor,
  type TesterFeedbackCategory,
  type TesterFeedbackContext,
  type TesterFeedbackSurface,
} from '@steward-app/steward-types';
import { btns } from '../components/buttons';
import { useFocusTrap } from '../lib/useFocusTrap';
import { collectTesterFeedbackContext, sendTesterFeedback, type TesterFeedbackOutcome } from './context';
import { TesterFeedbackScreenContext, type TesterFeedbackScreenState } from './screen';

/**
 * The tester feedback button (crossmarc/steward-app#2531). TEMPORARY, for RIPL's test round:
 * `packages/steward-types/src/testerFeedback.ts` says what to remove with it.
 *
 * Wraps one surface (the intake page, the onboarding screens). For a firm on
 * `TESTER_FEEDBACK_FIRM_IDS` it draws a small "Feedback" tab on the right edge; for any other
 * firm, or none, it renders its children and nothing else. A note goes to `POST
 * /tester-feedback`, which files it as a GitHub issue with the screen it came from, so a tester
 * never has to say where they were.
 *
 * Its own dialog rather than `components/folders/Modal`, because that module imports the
 * authenticated API client and the intake page must not (`intake/intakeBundleImports.test.ts`).
 */

const CATEGORIES: ReadonlyArray<{ value: TesterFeedbackCategory; label: string }> = [
  { value: 'problem', label: 'Something is wrong' },
  { value: 'idea', label: 'An idea' },
  { value: 'wording', label: 'Wording' },
];

const ICON_STROKE = 1.75;

const cls = {
  ...btns,
  tab:      'fixed right-0 top-1/2 z-[55] flex -translate-y-1/2 items-center gap-1.5 rounded-l-token border border-r-0 border-line-strong bg-surface px-2 py-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy shadow-panel cursor-pointer [writing-mode:vertical-rl] max-sm:min-w-11',
  overlay:  'fixed inset-0 z-[60] flex overflow-y-auto px-4 py-4',
  scrim:    'fixed inset-0 bg-ink/40',
  card:     'relative m-auto w-full max-w-[440px] rounded-token border border-line bg-surface p-6 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.3)]',
  close:    'absolute right-3 top-3 flex h-8 w-8 max-sm:right-1 max-sm:top-1 max-sm:h-11 max-sm:w-11 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-slate hover:text-navy',
  title:    'font-serif text-[17px] font-normal text-navy mb-1 pr-6 max-sm:pr-10',
  lead:     'text-[12px] leading-relaxed text-slate mb-4',
  label:    'block text-[10px] font-semibold text-slate mb-1.5 uppercase tracking-[0.1em]',
  chips:    'flex flex-wrap gap-1.5 mb-4',
  chip:     'px-3 py-1.5 text-[12px] font-semibold rounded-token border cursor-pointer max-sm:min-h-11',
  chipOn:   'border-bronze bg-bronze/12 text-navy',
  chipOff:  'border-line-strong bg-transparent text-slate',
  textarea: 'w-full min-h-[8rem] resize-y rounded-token border border-line-strong bg-transparent px-3 py-2 text-[14px] text-navy outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong disabled:opacity-50',
  withNote: 'mt-2 text-[11px] leading-relaxed text-slate',
  error:    'mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger',
  ok:       'flex items-start gap-2.5 rounded-token bg-bronze/10 px-3.5 py-3 text-[13px] text-navy',
  row:      'mt-4 flex flex-wrap items-center justify-end gap-2',
  // Off-screen, not `display: none`, so a form-filling bot still sees it.
  trap:     'absolute -left-[9999px] h-px w-px overflow-hidden',
};

const ERRORS: Record<Exclude<TesterFeedbackOutcome, 'sent'>, string> = {
  unavailable: 'We could not send your note just now. Your words are still here, so please try again in a minute.',
  invalid: 'We could not send that note. Please shorten it or remove any unusual characters, then try again.',
};

export function TesterFeedback({ firmId, surface, email, children }: {
  firmId: string | null | undefined;
  surface: TesterFeedbackSurface;
  /** The login's address when the whole surface knows it (onboarding). A screen that names an
   *  address of its own (the intake form, which knows it only below this) wins. */
  email?: string | null;
  children: ReactNode;
}) {
  const screenRef = useRef<TesterFeedbackScreenState>({ screen: '', email: null });
  const enabled = testerFeedbackEnabledFor(firmId);
  const [open, setOpen] = useState<TesterFeedbackContext | null>(null);
  // `useFocusTrap` hands focus back to the tab on close, so the tab stays mounted under the dialog.
  const close = useCallback(() => setOpen(null), []);

  return (
    <TesterFeedbackScreenContext.Provider value={screenRef}>
      {children}
      {enabled && firmId && (
        <>
          <button
            type="button"
            className={cls.tab}
            data-testid="tester-feedback-tab"
            // Read at the moment it opens, so the context is the screen under the dialog.
            onClick={() => setOpen(collectTesterFeedbackContext(surface, {
              screen: screenRef.current.screen,
              email: screenRef.current.email ?? email ?? null,
            }))}
          >
            <MessageSquare size={14} strokeWidth={ICON_STROKE} aria-hidden className="rotate-90" />
            Feedback
          </button>
          {open !== null && <FeedbackDialog firmId={firmId} context={open} onClose={close} />}
        </>
      )}
    </TesterFeedbackScreenContext.Provider>
  );
}

function FeedbackDialog({ firmId, context, onClose }: {
  firmId: string;
  context: TesterFeedbackContext;
  onClose: () => void;
}) {
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<TesterFeedbackCategory | null>(null);
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<TesterFeedbackOutcome | null>(null);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sending) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, sending]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (sending || !message.trim()) return;
    setSending(true);
    setOutcome(null);
    const result = await sendTesterFeedback({ message, category, firmId, context, website });
    setSending(false);
    setOutcome(result);
  }

  const where = context.screen || context.heading || context.path;

  return createPortal((
    <div
      className={cls.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={trapRef}
      data-tester-feedback
    >
      <div className={cls.scrim} onClick={() => { if (!sending) onClose(); }} aria-hidden />
      <div className={cls.card}>
        <button type="button" onClick={onClose} aria-label="Close" className={cls.close} disabled={sending}>
          <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </button>
        <div id={titleId} className={cls.title}>Send feedback</div>

        {outcome === 'sent' ? (
          <div>
            <div className={cls.ok} role="status">
              <Check size={16} strokeWidth={ICON_STROKE} aria-hidden className="mt-0.5 shrink-0 text-bronze" />
              <span>Thank you. Your note is with the Crossmarc team.</span>
            </div>
            <div className={cls.row}>
              <button
                type="button"
                className={cls.btn}
                onClick={() => { setMessage(''); setCategory(null); setOutcome(null); }}
              >
                Send another
              </button>
              <button type="button" className={cls.btnPrim} onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => void send(e)}>
            <p className={cls.lead}>
              Tell us anything that is confusing, broken or worded oddly. We note which screen you
              are viewing, so there is no need to describe it.
            </p>

            <span className={cls.label}>What kind of note? (optional)</span>
            <div className={cls.chips}>
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  aria-pressed={category === c.value}
                  className={`${cls.chip} ${category === c.value ? cls.chipOn : cls.chipOff}`}
                  onClick={() => setCategory((was) => (was === c.value ? null : c.value))}
                  disabled={sending}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <label className={cls.label} htmlFor={messageId}>Your note</label>
            <textarea
              id={messageId}
              className={cls.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, TESTER_FEEDBACK_MESSAGE_MAX))}
              disabled={sending}
              autoFocus
            />
            <p className={cls.withNote} data-testid="tester-feedback-sent-with">
              {'Sent with your note: this screen ('}{where}{') and its web address, your device, window size and browser type'}
              {context.email ? ', your sign-in address' : ''}
              {' and the app version. Never your answers.'}
            </p>

            <div className={cls.trap} aria-hidden>
              {/* Labelled and named so a browser's autofill has nothing to match: an autofilled
                  trap would drop a real tester's note while showing it as sent. The JSON key is
                  still `INTAKE_HONEYPOT_FIELD`. */}
              <label>
                Leave this empty
                <input
                  name="tf-leave-empty"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </label>
            </div>

            {outcome !== null && <div className={cls.error} role="alert">{ERRORS[outcome]}</div>}

            <div className={cls.row}>
              <button type="button" className={cls.btn} onClick={onClose} disabled={sending}>Cancel</button>
              <button type="submit" className={cls.btnPrim} disabled={sending || !message.trim()}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  ), document.body);
}
